import { useState, useEffect, useCallback, useRef } from 'react'
import { apiService } from '@/service/api'
import type { ConnectedGraphsResponse } from '@/types/connected-graphs'

interface UseConnectedGraphsReturn {
  data: ConnectedGraphsResponse | null
  loading: boolean
  error: Error | null
  reload: () => void
}

/**
 * Fetch the connected-P&IDs aggregation for a graph
 * (GET /v1/graph/:graphId/connected). Pass enabled=false to defer the
 * fetch until the consuming panel is actually open.
 */
export function useConnectedGraphs(
  graphId: string | undefined,
  enabled = true
): UseConnectedGraphsReturn {
  const [data, setData] = useState<ConnectedGraphsResponse | null>(null)
  // Start in the loading state when a fetch will fire immediately, so the
  // panel shows the spinner on first render instead of flashing the
  // "No connected P&IDs found" empty state for one frame.
  const [loading, setLoading] = useState(enabled && !!graphId)
  const [error, setError] = useState<Error | null>(null)
  const fetchIdRef = useRef(0)
  // fetchIdRef alone only discards STALE responses — on unmount its value is
  // unchanged, so an in-flight response still ran setData/setError/setLoading on a
  // gone component. React's dispatchSetState reads `window` on the way to
  // scheduling, so once vitest has torn the jsdom environment down that surfaces as
  // an unhandled rejection ("ReferenceError: window is not defined" via
  // getCurrentEventPriority) which fails the whole run even though every test
  // passed. Assigned in the effect body, not at useRef, so a StrictMode remount
  // re-arms it instead of staying false forever.
  const mountedRef = useRef(true)

  const load = useCallback(() => {
    if (!graphId || !enabled) {
      setData(null)
      setError(null)
      return
    }
    const id = ++fetchIdRef.current
    setLoading(true)
    setError(null)
    apiService
      .getConnectedGraphs(graphId)
      .then((res) => {
        if (mountedRef.current && id === fetchIdRef.current) setData(res)
      })
      .catch((err) => {
        if (mountedRef.current && id === fetchIdRef.current) {
          setData(null)
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      })
      .finally(() => {
        if (mountedRef.current && id === fetchIdRef.current) setLoading(false)
      })
  }, [graphId, enabled])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return { data, loading, error, reload: load }
}
